/**
 * DataBest — Caregiver Performance Detail
 * Shows KPI cards + radar + trend + assignments timeline (basic list for v1)
 * for a single caregiver in the selected period.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, RefreshCw, Users2, Download } from "lucide-react";
import { caregiverPerfApi } from "../api/caregiverPerfApi";
import PerfPeriodSelector, {
    buildDefaultPeriod,
} from "../components/PerfPeriodSelector";
import PerfKpiCards from "../components/PerfKpiCards";
import PerfRadarChart from "../components/PerfRadarChart";
import PerfPieChart from "../components/PerfPieChart";
import PerfAnomalyPanel from "../components/PerfAnomalyPanel";
import PerfAiNarrative from "../components/PerfAiNarrative";
import AssignmentRoleBadge from "@/features/assignments/components/AssignmentRoleBadge";
import useAuthStore from "@/store/authStore";
import { formatDateUS, todayISO } from "@/lib/dates";

const CaregiverPerfDetailPage = () => {
    const { caregiver_id } = useParams();
    const navigate = useNavigate();
    const [search] = useSearchParams();
    const currentUser = useAuthStore((s) => s.user);
    const selfView = currentUser?.id === caregiver_id;

    const initialPeriod = React.useMemo(() => {
        const from = search.get("from");
        const to = search.get("to");
        if (from && to) return { period_start: from, period_end: to };
        return buildDefaultPeriod(30);
    }, [search]);

    const [period, setPeriod] = useState(initialPeriod);
    const [detail, setDetail] = useState(null);
    const [peerKpis, setPeerKpis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [narrativeText, setNarrativeText] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setPeerKpis(null);
        try {
            const data = await caregiverPerfApi.getDetail(caregiver_id, period);
            setDetail(data);
        } catch (err) {
            toast.error(err?.message || "Could not load report");
            setDetail(null);
        } finally {
            setLoading(false);
        }
    }, [caregiver_id, period]);

    useEffect(() => {
        load();
    }, [load]);

    // Lazy-load peer benchmark after the main detail view has rendered so the
    // initial page load stays fast even in production (avoids Cloudflare
    // origin timeout on aggregated peer computations).
    useEffect(() => {
        if (!detail || selfView) return;
        let cancelled = false;
        caregiverPerfApi
            .getPeer(caregiver_id, period)
            .then((res) => {
                if (!cancelled) setPeerKpis(res?.peer_kpis || null);
            })
            .catch(() => {
                if (!cancelled) setPeerKpis(null);
            });
        return () => {
            cancelled = true;
        };
    }, [caregiver_id, period, detail, selfView]);

    const handleDownloadPdf = async () => {
        setDownloadingPdf(true);
        try {
            const { blob, filename } = await caregiverPerfApi.downloadPdf(
                caregiver_id,
                {
                    period_start: period.period_start,
                    period_end: period.period_end,
                    narrative_text: narrativeText || null,
                },
            );
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            toast.success("PDF downloaded");
        } catch (err) {
            toast.error(err?.message || "Download failed");
        } finally {
            setDownloadingPdf(false);
        }
    };

    if (loading && !detail) {
        return (
            <div className="flex h-64 items-center justify-center text-slate-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Calculating KPIs…
            </div>
        );
    }
    if (!detail) {
        return (
            <div className="mx-auto max-w-5xl p-8 text-center text-slate-500">
                Report not available.
            </div>
        );
    }

    return (
        <div
            className="mx-auto max-w-6xl space-y-4"
            data-testid={`caregiver-perf-detail-${caregiver_id}`}
        >
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
            >
                {/* Header */}
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
                    <div className="min-w-0">
                        <button
                            type="button"
                            onClick={() =>
                                navigate("/reports/caregiver-performance")
                            }
                            className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-brand-blue-700 hover:underline"
                            data-testid="perf-back-btn"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">Caregiver Performance</span>
                            <span className="sm:hidden">Back</span>
                        </button>
                        <h1 className="mt-1 truncate font-heading text-2xl font-bold text-brand-blue-950 sm:text-3xl">
                            {detail.caregiver_name}
                        </h1>
                        <p className="mt-0.5 text-xs text-slate-500 sm:text-sm">
                            <span className="hidden sm:inline">{detail.caregiver_email} · </span>
                            Period{" "}
                            <span className="font-mono">
                                {formatDateUS(detail.period_start)}
                            </span>{" "}
                            →{" "}
                            <span className="font-mono">
                                {formatDateUS(detail.period_end)}
                            </span>
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handleDownloadPdf}
                            disabled={downloadingPdf}
                            data-testid="perf-download-pdf-btn"
                            className="inline-flex items-center gap-1 rounded-lg border border-brand-blue-200 bg-brand-blue-50 px-2.5 py-1.5 text-xs font-bold text-brand-blue-800 hover:bg-brand-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                            title={narrativeText ? "Download PDF (includes generated Coaching Narrative)" : "Download PDF"}
                        >
                            {downloadingPdf ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <Download className="h-3.5 w-3.5" />
                            )}
                            <span className="hidden sm:inline">
                                {downloadingPdf ? "Preparing…" : "Download PDF"}
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={load}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                            data-testid="perf-refresh-btn"
                        >
                            <RefreshCw
                                className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
                            />
                            <span className="hidden sm:inline">Refresh</span>
                        </button>
                    </div>
                </div>

                {/* Period selector */}
                <div className="mt-4 rounded-2xl border border-slate-200 bg-white/85 p-3 backdrop-blur">
                    <PerfPeriodSelector value={period} onChange={setPeriod} />
                </div>
            </motion.div>

            {/* KPI cards */}
            <PerfKpiCards kpis={detail.kpis} />

            {/* Charts row: Total Records pie · % On-Time pie · Performance Shape radar */}
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                <PerfPieChart
                    testId="perf-pie-total-records"
                    title="Total Records"
                    data={[
                        {
                            name: "Completed",
                            value: detail.kpis.records_completed ?? detail.kpis.total_measurements ?? 0,
                            color: "#0d9488",
                        },
                        {
                            name: "Not Completed",
                            value: detail.kpis.records_not_completed ?? detail.kpis.missed_tasks ?? 0,
                            color: "#ea580c",
                        },
                    ]}
                />
                <PerfPieChart
                    testId="perf-pie-ontime"
                    title="% OnTime"
                    data={[
                        {
                            name: "OnTime",
                            value: detail.kpis.on_time_count ??
                                Math.max(0, (detail.kpis.records_completed ?? 0) - (detail.kpis.backlog_completions ?? 0)),
                            color: "#0f766e",
                        },
                        {
                            name: "Not Ontime",
                            value: detail.kpis.not_on_time_count ?? detail.kpis.backlog_completions ?? 0,
                            color: "#ea580c",
                        },
                    ]}
                />
                <PerfRadarChart
                    kpis={detail.kpis}
                    peerKpis={selfView ? null : peerKpis}
                />
            </div>

            {/* Anomaly panel */}
            <PerfAnomalyPanel flags={detail.flags} />

            {/* AI narrative (Fase G) */}
            <PerfAiNarrative
                caregiverId={caregiver_id}
                period={period}
                selfView={selfView}
                onNarrativeChange={setNarrativeText}
            />

            {/* Assignments in period */}
            <div
                className="rounded-2xl border border-slate-200 bg-white shadow-sm"
                data-testid="perf-assignments"
            >
                <div className="border-b border-slate-100 px-4 py-2.5">
                    <p className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-slate-500">
                        <Users2 className="h-3.5 w-3.5" />
                        Assignments in this period
                    </p>
                </div>
                {detail.assignments.length === 0 ? (
                    <p className="p-6 text-center text-xs text-slate-400">
                        No assignments overlap with this period.
                    </p>
                ) : (
                    <>
                        {/* Desktop table */}
                        <div className="hidden overflow-x-auto md:block">
                            <table className="min-w-full divide-y divide-slate-100 text-sm">
                                <thead className="bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-500">
                                    <tr>
                                        <th className="px-3 py-2 text-left">Individual</th>
                                        <th className="px-3 py-2 text-left">Role</th>
                                        <th className="px-3 py-2 text-left">Window</th>
                                        <th className="px-3 py-2 text-right">Days in period</th>
                                        <th className="px-3 py-2 text-right">Total Records</th>
                                        <th className="px-3 py-2 text-right">% Records Completed</th>
                                        <th className="px-3 py-2 text-right">% On-Time</th>
                                        <th className="px-3 py-2 text-right">% Coverage Gap</th>
                                        <th className="px-3 py-2 text-right">% Report Quality</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {detail.assignments.map((a) => (
                                        <tr key={a.id} className="hover:bg-slate-50">
                                            <td className="px-3 py-2 font-semibold text-slate-800">
                                                {a.individual_name}
                                            </td>
                                            <td className="px-3 py-2">
                                                <AssignmentRoleBadge role={a.role} />
                                            </td>
                                            <td className="px-3 py-2 text-slate-700">
                                                <span className="font-mono text-[11px]">
                                                    {formatDateUS(a.effective_from)}
                                                </span>
                                                <span className="mx-1 text-slate-400">→</span>
                                                <span className="font-mono text-[11px]">
                                                    {a.effective_to ? formatDateUS(a.effective_to) : "—"}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-right tabular-nums">{a.days_in_period}</td>
                                            <td className="px-3 py-2 text-right font-bold tabular-nums">
                                                {a.total_records ?? "—"}
                                            </td>
                                            <td className="px-3 py-2 text-right tabular-nums">
                                                {a.pct_records_completed != null ? `${a.pct_records_completed}%` : "—"}
                                            </td>
                                            <td className="px-3 py-2 text-right tabular-nums">
                                                {a.pct_on_time != null ? `${a.pct_on_time}%` : "—"}
                                            </td>
                                            <td className="px-3 py-2 text-right tabular-nums">
                                                {a.pct_coverage_gap != null ? (
                                                    <span className={a.pct_coverage_gap > 30 ? "font-bold text-amber-700" : "text-slate-700"}>
                                                        {a.pct_coverage_gap}%
                                                    </span>
                                                ) : "—"}
                                            </td>
                                            <td className="px-3 py-2 text-right tabular-nums">
                                                {a.pct_report_quality != null ? `${a.pct_report_quality}%` : "—"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {/* Mobile cards */}
                        <div className="space-y-2 p-2 md:hidden">
                            {detail.assignments.map((a) => (
                                <div key={a.id} className="rounded-lg border border-slate-200 bg-white p-3">
                                    <div className="flex items-start justify-between gap-2">
                                        <p className="min-w-0 truncate text-sm font-bold text-slate-900">
                                            {a.individual_name}
                                        </p>
                                        <AssignmentRoleBadge role={a.role} />
                                    </div>
                                    <p className="mt-1 font-mono text-[11px] text-slate-500">
                                        {formatDateUS(a.effective_from)} →{" "}
                                        {a.effective_to ? formatDateUS(a.effective_to) : "—"} · {a.days_in_period}d
                                    </p>
                                    <div className="mt-2 grid grid-cols-3 gap-2 border-t border-slate-100 pt-2 text-[11px]">
                                        <div>
                                            <p className="text-[9px] font-black uppercase tracking-wider text-slate-400">Total Rec</p>
                                            <p className="font-bold tabular-nums">{a.total_records ?? "—"}</p>
                                        </div>
                                        <div>
                                            <p className="text-[9px] font-black uppercase tracking-wider text-slate-400">% Compl</p>
                                            <p className="font-bold tabular-nums">{a.pct_records_completed != null ? `${a.pct_records_completed}%` : "—"}</p>
                                        </div>
                                        <div>
                                            <p className="text-[9px] font-black uppercase tracking-wider text-slate-400">% On-Time</p>
                                            <p className="font-bold tabular-nums">{a.pct_on_time != null ? `${a.pct_on_time}%` : "—"}</p>
                                        </div>
                                        <div>
                                            <p className="text-[9px] font-black uppercase tracking-wider text-slate-400">% Gap</p>
                                            <p className={`font-bold tabular-nums ${a.pct_coverage_gap != null && a.pct_coverage_gap > 30 ? "text-amber-700" : ""}`}>
                                                {a.pct_coverage_gap != null ? `${a.pct_coverage_gap}%` : "—"}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-[9px] font-black uppercase tracking-wider text-slate-400">% Quality</p>
                                            <p className="font-bold tabular-nums">{a.pct_report_quality != null ? `${a.pct_report_quality}%` : "—"}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CaregiverPerfDetailPage;
